import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ShieldAlert } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { BillingReadiness } from '@/hooks/useBillingReadiness';
import { useTranslation } from 'react-i18next';

// Plain-language labels for every field code the readiness RPC may return.
// Keep these human-readable — they appear directly in the user-facing missing-fields list.
const FIELD_LABEL_KEYS: Record<string, string> = {
  buyer_type: 'hire.billingGate.fields.buyerType',
  buyer_type_required: 'hire.billingGate.fields.buyerType',
  // Identity
  legal_name: 'hire.billingGate.fields.legalName',
  legal_name_required: 'hire.billingGate.fields.legalName',
  legal_name_invalid: 'hire.billingGate.fields.legalNameInvalid',

  // A B2BH profile requires a valid VAT number; B2C routes do not use it.
  vat_number: 'hire.billingGate.fields.vatNumber',
  vat_number_format: 'hire.billingGate.fields.vatInvalid',
  vat_required: 'hire.billingGate.fields.vatNumber',
  vat_invalid: 'hire.billingGate.fields.vatInvalid',

  // Additional commercial identifier (required for B2B)
  additional_id_scheme: 'hire.billingGate.fields.commercialIdType',
  additional_id_value: 'hire.billingGate.fields.commercialIdNumber',
  commercial_registration_scheme: 'hire.billingGate.fields.commercialIdType',
  commercial_registration_value: 'hire.billingGate.fields.commercialIdNumber',
  commercial_registration_value_format: 'hire.billingGate.fields.commercialIdNumberInvalid',
  business_identifier_scheme: 'hire.billingGate.fields.businessCommercialIdType',
  alt_id_scheme_required: 'hire.billingGate.fields.commercialIdType',
  alt_id_value_required: 'hire.billingGate.fields.commercialIdNumber',
  alt_id_pair_incomplete: 'hire.billingGate.fields.commercialIdPair',
  alt_id_scheme_invalid: 'hire.billingGate.fields.commercialIdTypeInvalid',
  alt_id_value_invalid: 'hire.billingGate.fields.commercialIdNumberInvalid',
  buyer_alt_id_scheme_not_business:
    'hire.billingGate.fields.businessCommercialIdType',

  // Address
  street_name: 'hire.billingGate.fields.streetName',
  street_required: 'hire.billingGate.fields.streetName',
  district: 'hire.billingGate.fields.district',
  district_required: 'hire.billingGate.fields.district',
  city_name: 'hire.billingGate.fields.city',
  city_required: 'hire.billingGate.fields.city',
  postal_zone: 'hire.billingGate.fields.postalZone',
  postal_zone_required: 'hire.billingGate.fields.postalZone',
  country_subentity: 'hire.billingGate.fields.region',
  region_required: 'hire.billingGate.fields.region',
  country_code: 'hire.billingGate.fields.country',
  country_invalid: 'hire.billingGate.fields.countryInvalid',
  building_number: 'hire.billingGate.fields.buildingNumber',
  building_number_required: 'hire.billingGate.fields.buildingNumber',
  plot_identification: 'hire.billingGate.fields.plotIdentification',
  plot_identification_required: 'hire.billingGate.fields.plotIdentification',

  // Billing contact
  billing_profile: 'hire.billingGate.fields.billingProfile',
  billing_contact: 'hire.billingGate.fields.billingContact',
  no_billing_contact: 'hire.billingGate.fields.billingContact',
  billing_contact_user_id: 'hire.billingGate.fields.billingContact',
  billing_contact_role_invalid:
    'hire.billingGate.fields.billingContactRoleInvalid',
  identity_review: 'hire.billingGate.fields.identityReview',
  platform: 'hire.billingGate.fields.billingProfile',
  fork_seller: 'hire.billingGate.fields.billingProfile',
};

const REASON_HEADLINE_KEYS: Record<string, string> = {
  no_billing_profile: 'hire.billingGate.reasons.noBillingProfile',
  incomplete_billing_identity: 'hire.billingGate.reasons.incompleteBillingIdentity',
  b2b_identity_incomplete: 'hire.billingGate.reasons.incompleteBillingIdentity',
  buyer_alt_id_scheme_not_business:
    'hire.billingGate.reasons.businessCommercialIdType',
  no_billing_contact: 'hire.billingGate.reasons.noBillingContact',
  billing_contact_role_invalid:
    'hire.billingGate.reasons.billingContactRoleInvalid',
  identity_conflict_review_required:
    'hire.billingGate.reasons.identityConflictReviewRequired',
  readiness_check_failed: 'hire.billingGate.reasons.readinessCheckFailed',
};

export interface BillingGateProps {
  readiness: BillingReadiness | undefined;
  isLoading?: boolean;
  /** Where the remediation button takes the user. */
  remediationPath?: string;
  /** Override the headline. */
  title?: string;
}

/**
 * Layer-3 enforcement: surfaces the canonical 409 BILLING_INCOMPLETE state
 * to the user with the exact Fork-policy copy and a route to remediate.
 *
 * Render above any Pay / Upgrade / Top-up CTA. When `readiness.ready === true`
 * this component renders nothing.
 */
export function BillingGate({
  readiness,
  isLoading,
  remediationPath = '/settings/billing/identity',
  title,
}: BillingGateProps) {
  const { t } = useTranslation();
  if (isLoading || !readiness || readiness.ready) return null;

  const missing = readiness.missing_fields.map(
    (k) => t(FIELD_LABEL_KEYS[k] ?? 'hire.billingGate.fields.identityReview'),
  );
  const headline = title ?? (REASON_HEADLINE_KEYS[readiness.reason ?? ''] ? t(REASON_HEADLINE_KEYS[readiness.reason ?? '']) : t('hire.billingGate.defaultTitle'));
  const routeDescription = readiness.buyer_type === 'B2B'
    ? t('hire.billingGate.b2bDescription')
    : readiness.buyer_type === 'B2C'
      ? t('hire.billingGate.b2cDescription')
      : t('hire.billingGate.description');

  return (
    <Alert variant="destructive" className="border-destructive/40">
      <ShieldAlert className="h-4 w-4" />
      <AlertTitle>{headline}</AlertTitle>
      <AlertDescription className="space-y-3">
        <p className="text-sm">
          {routeDescription}
        </p>
        {missing.length > 0 && (
          <div className="text-sm">
            <p className="font-medium mb-1">{t('hire.billingGate.missingFields')}</p>
            <ul className="list-disc ms-5 space-y-0.5">
              {missing.map((label) => (
                <li key={label}>{label}</li>
              ))}
            </ul>
          </div>
        )}
        <Button asChild size="sm" variant="outline">
          <Link to={remediationPath}>{t('hire.billingGate.completeBillingIdentity')}</Link>
        </Button>
      </AlertDescription>
    </Alert>
  );
}
